/**
 * TeamSystem Impact - Header
 * Breadcrumbs + notifications
 */

import { useEffect } from 'react';
import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Menu as MenuIcon,
  NavigateNext as NavigateNextIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuthStore } from '../../../store/authStore';
import { useNotificationStore } from '../../../store/notificationStore';

// Breadcrumb mapping
const BREADCRUMB_MAP: Record<string, { label: string; labelIT: string }> = {
  'dashboard': { label: 'Dashboard', labelIT: 'Dashboard' },
  'inbox': { label: 'Notifications', labelIT: 'Notifiche' },
  'dma': { label: 'Double Materiality', labelIT: 'Doppia Materialità' },
  'catalog': { label: 'IRO Catalog', labelIT: 'Catalogo IRO' },
  'assessment': { label: 'Assessment', labelIT: 'Valutazione Stakeholder' },
  'matrix': { label: 'Materiality Matrix', labelIT: 'Matrice Materialità' },
  'data-capture': { label: 'Data Capture', labelIT: 'Raccolta Dati' },
  'reporting': { label: 'Reports', labelIT: 'Report' },
  'taxonomy': { label: 'EU Taxonomy', labelIT: 'Tassonomia EU' },
  'audit': { label: 'Audit Trail', labelIT: 'Audit Trail' },
  'settings': { label: 'Settings', labelIT: 'Impostazioni' },
  'general': { label: 'General', labelIT: 'Generali' },
  'sustainability': { label: 'Sustainable Platform', labelIT: 'Piattaforma Sostenibile' },
};

interface HeaderProps {
  drawerWidth: number;
  onMenuClick: () => void;
}

export const Header = ({ drawerWidth: _drawerWidth, onMenuClick }: HeaderProps) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { utente, isAuthenticated } = useAuthStore();
  const { unreadCount, startPolling, stopPolling } = useNotificationStore();

  useEffect(() => {
    if (isAuthenticated) {
      startPolling();
    }
    return () => stopPolling();
  }, [isAuthenticated, startPolling, stopPolling]);

  // Generate breadcrumbs from current path
  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const crumbs: { label: string; path: string; isLast: boolean }[] = [];
    let currentPath = '';

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const mapping = BREADCRUMB_MAP[segment];
      if (mapping) {
        crumbs.push({
          label: i18n.language === 'it' ? mapping.labelIT : mapping.label,
          path: currentPath,
          isLast: index === pathSegments.length - 1,
        });
      }
    });

    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        width: '100%',
        left: 0,
        height: 56,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ minHeight: 56, px: { xs: 2, md: 3 } }}>
        {/* Mobile menu button */}
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMenuClick}
          aria-label="Apri menu di navigazione"
          sx={{
            mr: 2,
            display: { md: 'none' },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Breadcrumbs */}
        <Box sx={{ flexGrow: 1 }}>
          {breadcrumbs.length > 0 ? (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
              sx={{ '& .MuiBreadcrumbs-li': { lineHeight: 1 } }}
            >
              {breadcrumbs.map((crumb) => (
                crumb.isLast ? (
                  <Typography
                    key={crumb.path}
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {crumb.label}
                  </Typography>
                ) : (
                  <Link
                    key={crumb.path}
                    component={RouterLink}
                    to={crumb.path}
                    underline="hover"
                    color="text.secondary"
                    variant="body2"
                    sx={{ '&:hover': { color: 'primary.main' } }}
                  >
                    {crumb.label}
                  </Link>
                )
              ))}
            </Breadcrumbs>
          ) : (
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {utente?.azienda?.ragioneSociale || 'TeamSystem Impact'}
            </Typography>
          )}
        </Box>

        {/* Notification bell */}
        <IconButton
          color="inherit"
          onClick={() => navigate('/inbox')}
          size="small"
          aria-label="Visualizza notifiche"
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};
